import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const Pill = ({ children, tone = 'gray' }) => (
    <span className={
        `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ` +
        (tone === 'primary' ? 'bg-primary/10 text-primary border-primary/20' : '') +
        (tone === 'success' ? 'bg-green-50 text-green-700 border-green-200' : '') +
        (tone === 'danger' ? 'bg-red-50 text-red-700 border-red-200' : '') +
        (tone === 'gray' ? 'bg-gray-50 text-gray-700 border-gray-200' : '')
    }>
        {children}
    </span>
);

const Stat = ({ label, value }) => (
    <div className="p-4 rounded-xl border border-gray-100 bg-white">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</div>
        <div className="text-gray-900 font-semibold">{value}</div>
    </div>
);

const Accordion = ({ title, count, children, defaultOpen = true }) => {
    const [open, setOpen] = React.useState(defaultOpen);
    return (
        <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-4 sm:px-5 py-3 text-left hover:bg-gray-50"
            >
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{title}</span>
                    <Pill tone="gray">{count}</Pill>
                </div>
                <svg className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className="border-t border-gray-100">
                    {children}
                </div>
            )}
        </div>
    );
};

const ItemsList = ({ rows, kind }) => {
    if (!rows || !Array.isArray(rows) || rows.length === 0) {
        return <div className="p-5 text-sm text-gray-500">No {kind} items</div>;
    }

    return (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3">
            {rows.map((row, idx) => {
                const ref = row[kind];
                return (
                    <div key={row._id || idx} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 hover:shadow-sm transition-shadow">
                        <div>
                            <div className="text-sm font-medium text-gray-900">{ref?.name || '-'}</div>
                            <div className="text-xs text-gray-500 mt-0.5">Unit: {row?.unitType || ref?.unitType || '-'}</div>
                        </div>
                        <Pill tone="primary">{row?.quantity || '-'}</Pill>
                    </div>
                );
            })}
        </div>
    );
};

const MenuDetailsModal = ({ isOpen, onClose, item }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const totals = useMemo(() => {
        const groups = ['vegetable', 'grocery', 'dairy', 'farsan', 'vasan'];
        const result = {};
        groups.forEach(g => {
            const list = item?.items?.[g];
            result[g] = Array.isArray(list) ? list.length : 0;
        });
        result.total = Object.values(result).reduce((a, b) => a + b, 0);
        return result;
    }, [item]);

    if (!isOpen || !item) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>

            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-5xl max-h[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl">
                    <div className="relative">
                        <div className="h-16 bg-gradient-to-r from-primary/90 to-primary"></div>
                        <div className="absolute inset-x-0 top-0 px-6 py-4 flex items-center justify-between text-white">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-semibold truncate">{item?.menuName}</h3>
                                    {item?.menuNameEn && (
                                        <span className="text-sm opacity-90 truncate">({item.menuNameEn})</span>
                                    )}
                                </div>
                                <div className="text-xs opacity-90 mt-0.5 truncate">
                                    {t('Category')}: {item?.category?.categoryName || '-'} {item?.category?.categoryNameEn ? `(${item?.category?.categoryNameEn})` : ''}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Pill tone={item?.isActive ? 'success' : 'gray'}>
                                    {item?.isActive ? (t('Active') || 'Active') : (t('Inactive') || 'Inactive')}
                                </Pill>
                                <button
                                    onClick={() => {
                                        onClose?.();
                                        navigate('/addmenu', { state: { selectedMenu: item, isEdit: true } });
                                    }}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/90 hover:bg-white text-primary font-medium"
                                    title={t('btnUpdate') || 'Edit'}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    <span>{t('btnUpdate') || 'Edit'}</span>
                                </button>
                                <button
                                    onClick={onClose}
                                    className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
                                    title={t('btnClose') || 'Close'}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="px-6 pb-6 pt-5 overflow-y-auto max-h-[calc(90vh-64px)]">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-1 space-y-4">
                                <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                                    <div className="p-4 border-b border-gray-100">
                                        <div className="text-sm font-semibold text-gray-900">{t('Summary') || 'Summary'}</div>
                                    </div>
                                    <div className="p-4 grid grid-cols-2 gap-3">
                                        <Stat label={t('price')} value={item?.price ? `₹${item.price}` : '-'} />
                                        <Stat label={t('Items') || 'Items'} value={totals.total} />
                                        <Stat label={t('Vegetables') || 'Vegetables'} value={totals.vegetable} />
                                        <Stat label={t('Groceries') || 'Groceries'} value={totals.grocery} />
                                        <Stat label={t('Dairy') || 'Dairy'} value={totals.dairy} />
                                    </div>
                                </div>
                            </div>

                            <div className="lg:col-span-2 space-y-4">
                                <Accordion title={t('Vegetables') || 'Vegetables'} count={totals.vegetable} defaultOpen={false}>
                                    <ItemsList rows={item?.items?.vegetable} kind="vegetable" />
                                </Accordion>

                                <Accordion title={t('Groceries') || 'Groceries'} count={totals.grocery} defaultOpen={false}>
                                    <ItemsList rows={item?.items?.grocery} kind="grocery" />
                                </Accordion>

                                <Accordion title={t('Dairy') || 'Dairy'} count={totals.dairy}  defaultOpen={false}>
                                    <ItemsList rows={item?.items?.dairy} kind="dairy" />
                                </Accordion>

                                <Accordion title={t('Farsan') || 'Farsan'} count={totals.farsan} defaultOpen={false}>
                                    <ItemsList rows={item?.items?.farsan} kind="farsan" />
                                </Accordion>

                                <Accordion title={t('Vasan') || 'Vasan'} count={totals.vasan} defaultOpen={false}>
                                    <ItemsList rows={item?.items?.vasan} kind="vasan" />
                                </Accordion>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MenuDetailsModal; 